import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { TicketIntegrationNoteItem } from 'src/app/api/models';
import { TicketService } from 'src/app/api/services';

/**
 * Note per le integrazioni di un ticket, in versione compatta per la pagina di dettaglio: nuova nota
 * (se consentito) e note più recenti per prime, con "Mostra altre" per scorrere le precedenti.
 * Filtri e ricerca stanno nella finestra completa (ModalTicketIntegrationNotesComponent), che il
 * padre apre con l'evento `openAll`.
 */
@Component({
  selector: 'app-ticket-integration-notes-panel',
  templateUrl: './ticket-integration-notes-panel.component.html',
  styleUrls: ['./ticket-integration-notes-panel.component.scss'],
})
export class TicketIntegrationNotesPanelComponent implements OnChanges {
  readonly maxNoteLength = 4000;
  readonly pageSize = 10;

  @Input({ required: true }) ticketId!: number;
  /** false se il ticket è chiuso/annullato o l'utente non può modificarlo. */
  @Input() canAdd = false;
  @Output() openAll = new EventEmitter<void>();

  notes: TicketIntegrationNoteItem[] = [];
  total = 0;
  loading = false;
  saving = false;

  newNote = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required, Validators.maxLength(this.maxNoteLength)],
  });

  constructor(
    private ticketService: TicketService,
    private messageService: MessageService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['ticketId']) {
      this.reload();
    }
  }

  get hasMore() {
    return this.notes.length < this.total;
  }

  authorName(note: TicketIntegrationNoteItem): string {
    if ((note as any).from_hda) return 'HDA';
    const u = note.related_user;
    return [u?.name, u?.last_name].filter(Boolean).join(' ') || u?.username || '-';
  }

  /** Riparte dalla prima pagina (le note nuove sono le più recenti). */
  reload() {
    this.notes = [];
    this.total = 0;
    this.loadPage(0);
  }

  loadMore() {
    this.loadPage(this.notes.length);
  }

  private loadPage(skip: number) {
    if (!this.ticketId || this.loading) return;
    this.loading = true;
    this.ticketService
      .ticketControllerFindIntegrationNotes({ id: this.ticketId, limit: this.pageSize, skip })
      .subscribe({
        next: res => {
          this.notes =
            skip === 0 ? (res.data?.list ?? []) : [...this.notes, ...(res.data?.list ?? [])];
          this.total = res.data?.count ?? 0;
          this.loading = false;
        },
        error: err => {
          this.loading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Note integrazione',
            detail:
              typeof err?.error?.message === 'string'
                ? err.error.message
                : 'Impossibile caricare le note.',
          });
        },
      });
  }

  addNote() {
    const note = this.newNote.value.trim();
    if (!note || this.newNote.invalid || this.saving) return;
    this.saving = true;
    this.ticketService
      .ticketControllerCreateIntegrationNote({ id: this.ticketId, body: { note } })
      .subscribe({
        next: () => {
          this.saving = false;
          this.newNote.reset('');
          this.reload();
          this.messageService.add({
            severity: 'success',
            summary: 'Note integrazione',
            detail: 'Nota aggiunta.',
          });
        },
        error: err => {
          this.saving = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Note integrazione',
            detail:
              typeof err?.error?.message === 'string'
                ? err.error.message
                : 'Impossibile aggiungere la nota.',
          });
        },
      });
  }
}
